import { useEffect, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import type { DocuSignEnvelope } from '@/lib/types';

/*
 * The signature envelopes raised against ONE quote.
 *
 * Lifted out of `EnvelopePanel` on 2026-09-03, where this read and its state
 * had lived since the panel was written. The quote detail page's HEADER now
 * offers the same three controls the panel does — Send for signature, Resend
 * for signature, and Change signer and resend — and it cannot choose between
 * offering a SEND and offering a RESEND without knowing whether an envelope is
 * already outstanding. That answer sat inside the panel's own `useState`, where
 * nothing above it could reach it.
 *
 * ⚠️ **WHAT THIS HOOK GUARANTEES IS ONE DEFINITION, NOT ONE LIST — and the
 * difference has to be stated, because the reassuring version is nearly true.**
 * Every caller now agrees on what is asked for, how it is ordered, what a 404
 * means and what `null` means, so a header and a panel cannot answer the same
 * question by two different rules. They still hold two SEPARATE READS, taken
 * moments apart. See the caching note below for what to do about that and, more
 * importantly, what not to do.
 *
 * ⚠️ **This hook renders nothing and words nothing, deliberately.** It returns
 * data. Every rule about how a DocuSign status may be DESCRIBED lives with the
 * surfaces that render it — `EnvelopePanel`, `EnvelopeStatusBadge`, and the
 * constants in `@/lib/docusign`. Three of those rules are worth naming here
 * only so nobody grows copy in this file to satisfy them:
 *
 *   - **A status may never be rendered as live.** There is no webhook. A
 *     server-side worker re-checks outstanding envelopes on a timer, roughly
 *     every five minutes, best-effort — it backs off after a failure and does
 *     not run at all where DocuSign is unconfigured. What a caller holds is a
 *     snapshot from the last check, and the next one is not guaranteed.
 *   - **A completed signature raises NOTHING.** It moves the quote to the
 *     accepted stage and attaches the signed PDF to the Xero quote. Billing is
 *     a separate step somebody takes afterwards. This app once carried the
 *     opposite claim across eight rendered strings.
 *   - **Whether signatures are binding is answered by `signaturesNotBinding()`
 *     in `@/lib/docusign`, from a CONNECTION READING**, never from the absence
 *     of one and never as an unconditional badge. Nothing on this shape can
 *     answer it, which is why nothing here tries.
 *
 * ## There is no query cache in this repo, and two callers means TWO fetches
 *
 * That is accepted and it is the state today: `EnvelopePanel` and the quote
 * header each call this, so opening a quote reads the envelope list twice. Both
 * are `GET`s against our own API — nothing here talks to DocuSign, which holds
 * the rate-limited quota — so the cost is one extra request per quote page.
 *
 * ⚠️ **If that ever matters, the fix is to LIFT THE CALL UP to
 * `QuoteDetailPage` and pass the state down to both — NOT to add a cache layer
 * here.** A cache buys one saved request and creates an invalidation question:
 * who clears it after a send, a void, a reminder, a status check, or a quote
 * the user navigated away from and came back to? Every wrong answer to that is
 * silent — a header offering "Send" over a live envelope, or a panel showing an
 * envelope somebody voided a minute ago. Lifting the call has no invalidation
 * question at all, because there is then only ever one read.
 */

/**
 * The list read, and whatever it last failed with.
 *
 * ⚠️ **This shape is depended on by more than one file.** The quote header and
 * `EnvelopePanel` both read it, so renaming or narrowing a member is a
 * coordinated change across call sites rather than a local edit.
 */
export interface QuoteEnvelopesState {
  /**
   * The envelopes, newest first — or `null` for **nothing has been read**.
   *
   * ⚠️ **`null` and `[]` are DIFFERENT FACTS and must not be coalesced where a
   * CONTROL is decided.** `[]` is *the server answered, and this quote has no
   * signature requests*, which is the fact that says a caller may offer **Send
   * for signature**. `null` is *we have not been told*: the first read is still
   * in flight, it failed, or the API has no DocuSign half mounted. A `?? []`
   * turns the second into the first, and what that looks like on screen is a
   * header offering "Send for signature" on a quote that already has a live
   * envelope. A second send while one is outstanding is a `409`; where it is
   * not refused, it puts two contracts in front of one customer, both looking
   * equally legitimate.
   *
   * ⚠️ **It is deliberately NOT emptied on a failed re-read.** A list read a
   * moment ago is still the last thing the server actually said, and dropping
   * it to show an error replaces a true answer with no answer. It comes back
   * ALONGSIDE `error`, never instead of it.
   */
  envelopes: DocuSignEnvelope[] | null;
  /**
   * The first read, or a `reloadKey` re-read, is in flight.
   *
   * ⚠️ **True on the first render**, before anything has been asked — the
   * initial state carries it rather than the effect setting it, so there is no
   * frame in which a caller sees `loading: false` with `envelopes: null` and
   * concludes the quote has none.
   */
  loading: boolean;
  /**
   * ⚠️ **A sibling of `envelopes`, never a replacement for it.** A re-read can
   * fail while the previous list is still on screen and still true, and both
   * facts need showing — `error ?? list` would silently drop whichever came
   * second. See the `??` rule in CLAUDE.md.
   */
  error: string | null;
  /**
   * The backend has not mounted its DocuSign half. A calm state to render, not
   * a red one — the same habit the Xero surface has for a 404.
   */
  notWired: boolean;
}

/**
 * Nothing asked yet.
 *
 * `loading: true` from the outset is what lets the effect skip a
 * `setState({loading: true})` of its own — see the effect.
 */
const IDLE: QuoteEnvelopesState = {
  envelopes: null,
  loading: true,
  error: null,
  notWired: false,
};

/**
 * Read the signature envelopes raised against one quote.
 *
 * @param quoteId   The quote they settle. `GET /docusign/envelopes?quoteId=N`.
 * @param reloadKey Bump to force a re-read — after the send dialog closes with
 *                  a `201`, or the void dialog with a `200`. Those dialogs are
 *                  owned by other screens, so they cannot hand a caller the row
 *                  and a re-read is the only way to learn what they did.
 */
export function useQuoteEnvelopes(quoteId: number, reloadKey: number): QuoteEnvelopesState {
  const [state, setState] = useState<QuoteEnvelopesState>(IDLE);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    /*
     * No `setState({loading: true})` here, deliberately: `IDLE` already
     * carries `loading: true`. This effect also re-runs on a `reloadKey` bump
     * — after a send, void or status check — and the previous list is KEPT on
     * screen while the re-read is in flight, the same trade the `.catch`
     * below makes on an error.
     */
    api
      /*
       * `order` is deliberately not sent: the server orders newest first and
       * a caller renders what it is given. Re-sorting client-side is how a
       * list quietly stops agreeing with its own contract. `perPage` is the
       * ceiling — the default of 50 would truncate silently, and a quote with
       * more envelopes than that is a quote worth seeing all of.
       */
      .listDocusignEnvelopes({ quoteId, perPage: 100 }, signal)
      .then(({ data }) => {
        if (signal.aborted) return;
        setState({ envelopes: data, loading: false, error: null, notWired: false });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        /*
         * 401 is left entirely to `api.ts`, which attempts one refresh and then
         * ends the session — a message here would contradict it midway through
         * signing out. 404 means the backend has not mounted its DocuSign half,
         * which is a state to render calmly rather than a failure.
         */
        if (caught instanceof ApiError && caught.status === 401) return;
        if (caught instanceof ApiError && caught.status === 404) {
          /*
           * ⚠️ **`envelopes` stays `null` here, and it used to be `[]`.** When
           * this state lived in `EnvelopePanel` the two were interchangeable,
           * because the not-wired branch renders above the list and the array
           * was never reached. They are not interchangeable for a caller that
           * decides a control: `[]` would say *this quote has no signature
           * requests, offer Send*, on an API that cannot answer the question at
           * all. Nothing was read, so nothing is claimed.
           */
          setState({ envelopes: null, loading: false, error: null, notWired: true });
          return;
        }
        setState((current) => ({
          /*
           * The previous list is KEPT. It is still the last thing the server
           * actually said, and dropping it to show an error replaces a true
           * answer with no answer. Both render.
           */
          envelopes: current.envelopes,
          loading: false,
          error:
            caught instanceof Error ? caught.message : 'Could not read the signature requests.',
          notWired: false,
        }));
      })
      .finally(() => {
        if (signal.aborted) return;
        setState((current) => ({ ...current, loading: false }));
      });

    return () => controller.abort();
  }, [quoteId, reloadKey]);

  return state;
}
